import { useState, useEffect } from "react";
import { useMediaQuery } from "@mantine/hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Stack,
  Text,
  Title,
  Alert,
  Button,
  Group,
  Loader,
  Center,
  Divider,
} from "@mantine/core";
import {
  IconFingerprint,
  IconDeviceMobile,
  IconBuildingHospital,
  IconMail,
  IconAlertCircle,
} from "@tabler/icons-react";
import { usePortalAuthStore } from "../../store/portalAuth.store";
import { physicianPortalService } from "../../services/physicianPortal.service";
import { physicianPasskeyService } from "../../services/physicianPasskey.service";
import { PortalPasswordRegistrationForm } from "../../components/portal/PortalPasswordRegistrationForm";

function getPasskeyErrorMessage(e: any): string {
  switch (e?.name) {
    case "NotAllowedError":
      return "Passkey setup was cancelled or timed out. Try again, or skip for now.";
    case "InvalidStateError":
      return "This device already has a passkey for your account. Try signing in instead.";
    case "NotSupportedError":
      return "Your browser doesn't support passkeys. Skip for now and set one up later from a supported browser.";
    case "SecurityError":
      return "Passkey setup was blocked for security reasons. Skip for now and try again later.";
    case "AbortError":
      return "Passkey setup was cancelled. Try again when you're ready.";
    default:
      return (
        e?.response?.data?.message ||
        "We couldn't register your passkey. Try again, or skip for now."
      );
  }
}

export function PortalRegisterPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const caseId = searchParams.get("caseId");
  const navigate = useNavigate();
  const login = usePortalAuthStore((s) => s.login);

  const [loading, setLoading] = useState(true);
  const [registrationData, setRegistrationData] = useState<{
    email: string;
    practiceName: string;
    subjectName: string | null;
    alreadyRegistered: boolean;
    isValid: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [skipLoading, setSkipLoading] = useState(false);

  const isMobile = useMediaQuery("(max-width: 480px)");

  useEffect(() => {
    if (!token) {
      setInvitationError("No invitation token provided");
      setLoading(false);
      return;
    }
    physicianPortalService
      .getRegistrationData(token)
      .then((data) => setRegistrationData(data))
      .catch(() => setInvitationError("Invalid or expired invitation link"))
      .finally(() => setLoading(false));
  }, [token]);

  const handlePasskeyRegister = async () => {
    setPasskeyLoading(true);
    setError(null);
    try {
      const result = await physicianPasskeyService.registerPasskey(
        token,
        "My Device"
      );
      login(result.sessionToken, result.email, result.practiceNames);
      navigate(caseId ? `/portal/report/${caseId}` : "/portal/dashboard");
    } catch (e: any) {
      setError(getPasskeyErrorMessage(e));
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSkip = async () => {
    setSkipLoading(true);
    setError(null);
    try {
      const result = await physicianPortalService.skipPasskey(token);
      login(result.sessionToken, result.email, result.practiceNames);
      navigate(caseId ? `/portal/report/${caseId}` : "/portal/dashboard");
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          "We couldn't activate your account. Please try again."
      );
    } finally {
      setSkipLoading(false);
    }
  };

  if (loading) {
    return (
      <Center h={300}>
        <Stack align="center" gap="sm">
          <Loader color="#04119A" />
          <Text
            size="sm"
            c="dimmed"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            Loading your invitation…
          </Text>
        </Stack>
      </Center>
    );
  }

  if (invitationError || !registrationData?.isValid) {
    return (
      <Box
        style={{
          minHeight: "calc(100vh - 64px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <Box style={{ width: "100%", maxWidth: 480 }}>
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="red"
            title="Invalid Invitation"
            variant="light"
          >
            {invitationError || "This invitation link is no longer valid."}
          </Alert>
        </Box>
      </Box>
    );
  }

  if (registrationData?.alreadyRegistered) {
    return (
      <Box
        style={{
          minHeight: "calc(100vh - 64px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <Box style={{ width: "100%", maxWidth: 480 }}>
          <Stack gap={4} mb="xl">
            <Text
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#10b981",
              }}
            >
              Welcome back
            </Text>
            <Title
              style={{
                fontFamily: "'DM Serif Display', serif",
                fontWeight: 400,
                fontSize: "2rem",
                color: "#1e1e3f",
                lineHeight: 1.15,
              }}
            >
              Confirm Portal Access
            </Title>
            <Group gap="xs" mt={6} wrap="wrap">
              <IconMail size={13} color="#6b7280" />
              <Text
                size="sm"
                c="dimmed"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                {registrationData.email}
              </Text>
            </Group>
          </Stack>

          <Box
            style={{
              background: "#ffffff",
              borderRadius: 14,
              border: "1px solid #e5e7f8",
              boxShadow:
                "0 4px 24px rgba(4,17,154,0.06), 0 1px 4px rgba(0,0,0,0.04)",
              padding: isMobile ? "20px" : "28px 32px",
            }}
          >
            <Stack gap="md">
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <IconBuildingHospital
                  size={18}
                  color="#04119A"
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Text
                  size="sm"
                  c="dimmed"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  You already have a Client Portal account. Confirm access to{" "}
                  <Text span fw={600} c="#1e1e3f">
                    {registrationData.subjectName ||
                      registrationData.practiceName ||
                      "this organization"}
                  </Text>{" "}
                  to see its reports alongside your existing ones.
                </Text>
              </Group>

              {error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  variant="light"
                >
                  {error}
                </Alert>
              )}

              <Button
                fullWidth
                size="md"
                loading={skipLoading}
                onClick={handleSkip}
                style={{
                  background:
                    "linear-gradient(135deg, #04119A 0%, #1a2cc4 100%)",
                  border: "none",
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontWeight: 500,
                }}
              >
                Confirm &amp; Continue
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .register-form-enter { animation: fadeSlideUp 0.4s ease-out forwards; }
      `}</style>

      <Box
        style={{
          minHeight: "calc(100vh - 64px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <Box
          className="register-form-enter"
          style={{ width: "100%", maxWidth: 480 }}
        >
          {/* Heading */}
          <Stack gap={4} mb="xl">
            <Text
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#10b981",
              }}
            >
              Invitation
            </Text>
            <Title
              style={{
                fontFamily: "'DM Serif Display', serif",
                fontWeight: 400,
                fontSize: "2rem",
                color: "#1e1e3f",
                lineHeight: 1.15,
              }}
            >
              Set Up Secure Access
            </Title>
            {registrationData && (
              <Group gap="xs" mt={6} wrap="wrap">
                <IconBuildingHospital size={15} color="#6b7280" />
                <Text
                  size="sm"
                  c="dimmed"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  {registrationData.subjectName ||
                    registrationData.practiceName}
                </Text>
                <Text size="sm" c="dimmed">
                  ·
                </Text>
                <IconMail size={13} color="#6b7280" />
                <Text
                  size="sm"
                  c="dimmed"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  {registrationData.email}
                </Text>
              </Group>
            )}
          </Stack>

          {/* Card */}
          <Box
            style={{
              background: "#ffffff",
              borderRadius: 14,
              border: "1px solid #e5e7f8",
              boxShadow:
                "0 4px 24px rgba(4,17,154,0.06), 0 1px 4px rgba(0,0,0,0.04)",
              padding: isMobile ? "20px" : "28px 32px",
            }}
          >
            <Stack gap="md">
              <Text
                size="sm"
                c="dimmed"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                Choose how you&apos;d like to sign in to the portal.
              </Text>

              <PortalPasswordRegistrationForm
                invitationToken={token}
                caseId={caseId}
              />

              <Divider label="Or use a passkey" labelPosition="center" />

              {/* Passkey feature list */}
              <Stack
                gap="xs"
                p="sm"
                style={{
                  background: "rgba(4,17,154,0.04)",
                  borderRadius: 8,
                }}
              >
                {[
                  {
                    icon: <IconFingerprint size={16} />,
                    label: "Touch ID or Face ID",
                  },
                  {
                    icon: <IconDeviceMobile size={16} />,
                    label: "Phone or hardware key",
                  },
                ].map((item, i) => (
                  <Group key={i} gap="xs">
                    <Box style={{ color: "#04119A" }}>{item.icon}</Box>
                    <Text
                      size="sm"
                      c="dimmed"
                      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                    >
                      {item.label}
                    </Text>
                  </Group>
                ))}
              </Stack>

              {error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  variant="light"
                >
                  {error}
                </Alert>
              )}

              <Button
                fullWidth
                size="md"
                leftSection={<IconFingerprint size={18} />}
                loading={passkeyLoading}
                disabled={skipLoading}
                onClick={handlePasskeyRegister}
                style={{
                  background:
                    "linear-gradient(135deg, #04119A 0%, #1a2cc4 100%)",
                  border: "none",
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontWeight: 500,
                }}
              >
                Register with Passkey
              </Button>

              <Divider />

              <Stack gap={4} align="center">
                <Button
                  variant="subtle"
                  size="sm"
                  loading={skipLoading}
                  disabled={passkeyLoading}
                  onClick={handleSkip}
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    color: "#04119A",
                  }}
                >
                  Skip for now
                </Button>
                <Text
                  size="xs"
                  ta="center"
                  c="dimmed"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  You can set up a passkey later from your account settings.
                </Text>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default PortalRegisterPage;
